import { h, FunctionComponent } from 'preact';
import { createPortal } from 'preact/compat';
import { useState } from 'preact/hooks';
import Cookies from 'js-cookie';
import { HugoCookie } from '@popularlab/enums';

import { Modal, ModalTitle, ModalContent } from '../modal';
import { Button, ButtonCta } from '../button';
import IconCcpa from '../../../assets/images/icons/ccpa.svg';
import Checkbox, { CheckboxVariant } from '../Checkbox/Checkbox';
import { setAccountFlag } from '../../actions/api';

const COOKIE_NAME = HugoCookie.HUGO_CONSUMER_DATA_SALE_OPT_OUT;
const COOKIE_EXPIRY_DAYS = 365;
const PRIVACY_POLICY_URL = '/legal#privacy-policy';
const COOKIES_POLICY_URL = '/legal#tracking-technologies';
const YOUR_CHOICES_URL = `/legal#your-choices`;

const getCookieDomain = () => {
    const url = new URL(window.location.href);
    const isLocalhost = url.hostname === 'localhost';

    if (isLocalhost) return 'localhost.com';

    // get top level domain (e.g. withhugo.com) to set cookie for all subdomains
    const domainParts = url.hostname.split('.');
    const topLevelDomain = domainParts.slice(-2).join('.');

    return topLevelDomain;
};

export const PrivacyChoice: FunctionComponent<{ className?: string }> = ({ className }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [optedOut, setOptedOut] = useState(false);

    const openModal = () => {
        const isOptedOut = Cookies.get(COOKIE_NAME) !== undefined;
        setOptedOut(isOptedOut);
        setModalVisible(true);
    };

    const handleSubmit = () => {
        if (optedOut) {
            Cookies.set(COOKIE_NAME, '1', {
                domain: getCookieDomain(),
                expires: COOKIE_EXPIRY_DAYS,
            });
        } else {
            Cookies.remove(COOKIE_NAME, { domain: getCookieDomain() });
        }

        setAccountFlag('is-consumer-data-sale-opt-out', optedOut).catch((e) => {
            if ('error' in e && e.error === 'HttpCsrfError') {
                // We didn't have a CSRF token, try one more time since one should have just been set
                setAccountFlag('is-consumer-data-sale-opt-out', optedOut).catch(() => {});
            }

            // ignore error, the user is very likely logged out, and this is only relevant
            // for logged in users
        });

        setModalVisible(false);
    };

    return (
        <span className={className}>
            <Button link icon onClick={openModal}>
                <span className="u-mr-1">Your Privacy Choices</span>
                <img src={IconCcpa} width={30} height={14} alt="CCPA icon" />
            </Button>

            {modalVisible &&
                createPortal(
                    <Modal onHide={() => setModalVisible(false)}>
                        <ModalTitle>Your Privacy Choices</ModalTitle>
                        <ModalContent>
                            <p>
                                <b>Hugo shares</b> personal information for cross contextual
                                advertising as defined by California law. California law provides a
                                right for California residents to opt-out of such sharing. To learn
                                more about this right, you may review our notice of{' '}
                                <a
                                    href={YOUR_CHOICES_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    California Privacy Rights.
                                </a>
                            </p>
                            <p>
                                <b>You may opt-out</b> of this sharing by selecting the checkbox
                                below and clicking the Submit button. Your opt-out will be stored as
                                a cookie on your device.*
                            </p>
                            <p>
                                If the checkbox below shows a checkmark, your opt-out request has
                                been honored.
                            </p>
                            <Checkbox
                                variant={CheckboxVariant.ALTERNATIVE}
                                className="u-mb-4"
                                name="data-sale-opt-out"
                                label="Do not share my personal information"
                                checked={optedOut}
                                onChange={() => setOptedOut(!optedOut)}
                            />
                            <p>
                                <b>For additional information</b> on our privacy practices, please
                                review our:
                            </p>
                            <p>
                                <a
                                    href={PRIVACY_POLICY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Privacy Policy
                                </a>
                                <span className="u-txt-gray-80 u-mx-1">|</span>
                                <a
                                    href={COOKIES_POLICY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Cookies Policy
                                </a>
                            </p>
                            <div className="c-modal__actions">
                                <ButtonCta onClick={handleSubmit} className="u-font-3">
                                    Submit
                                </ButtonCta>
                                <Button link onClick={() => setModalVisible(false)}>
                                    Cancel
                                </Button>
                            </div>
                            <p className="u-font-1 u-muted u-mt-6">
                                *This cookie is specific to a particular device and browser. If you
                                use different browsers or devices, you will need to opt-out on each.
                                The opt-out is facilitated via cookies. Therefore if you clear your
                                cookies you will need to opt-out again.
                            </p>
                        </ModalContent>
                    </Modal>,
                    document.body,
                )}
        </span>
    );
};
